/**
 * Date utilities centralized helper
 *
 * This module provides date formatting and relative time functions
 * that always use Spanish locale, ensuring consistency across the app.
 *
 * Using dayjs (modern, 2KB alternative to moment.js with similar API)
 * All functions use Spanish locale automatically via global config.
 */

import dayjs from 'dayjs';
import 'dayjs/locale/es';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Extend dayjs with plugins
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(calendar);
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.locale('es');

/**
 * Format a date in Spanish using dayjs's locale-aware formats
 *
 * @param date - Date to format (Date object, string, or timestamp)
 * @param format - Dayjs format string (default: 'LLL' = "23 de enero de 2026 12:56")
 * @returns Formatted date string in Spanish
 *
 * Common formats:
 * - 'LLL': "23 de enero de 2026 12:56"
 * - 'LL': "23 de enero de 2026"
 * - 'L': "23/01/2026"
 * - 'LT': "12:56"
 * - 'LTS': "12:56:45"
 * - 'DD/MM/YYYY HH:mm': "23/01/2026 12:56"
 */
export function formatDate(date: Date | string | number, format = 'LLL'): string {
  return dayjs(date).format(format);
}

/**
 * Get relative time from now in Spanish (e.g., "hace 2 horas", "en 3 días")
 *
 * @param date - Date to compare with now
 * @returns Relative time string in Spanish
 *
 * Examples:
 * - "hace unos segundos"
 * - "hace 2 minutos"
 * - "hace una hora"
 * - "hace 3 días"
 * - "en 2 horas" (for future dates)
 */
export function fromNowEs(date: Date | string | number): string {
  return dayjs(date).fromNow();
}

/**
 * Get relative time to a specific date in Spanish
 *
 * @param date - Target date
 * @returns Relative time string in Spanish (e.g., "en 2 horas")
 */
export function toNowEs(date: Date | string | number): string {
  return dayjs(date).toNow();
}

/**
 * Humanize a duration in Spanish
 *
 * @param seconds - Duration in seconds
 * @param withSuffix - Include "en" or "hace" prefix (default: true)
 * @returns Humanized duration string in Spanish
 *
 * Examples:
 * - humanizeDuration(120, false) => "2 minutos"
 * - humanizeDuration(120, true) => "en 2 minutos"
 * - humanizeDuration(3600, false) => "una hora"
 */
export function humanizeDuration(seconds: number, withSuffix = true): string {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '—';
  return dayjs.duration(seconds, 'seconds').humanize(withSuffix);
}

/**
 * Format uptime duration in Spanish
 *
 * @param seconds - Uptime in seconds
 * @returns Formatted uptime string (e.g., "hace 2 días", "hace 3 horas")
 */
export function formatUptime(seconds: number): string {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '—';
  return humanizeDuration(seconds, true);
}

/**
 * Get current datetime formatted in Spanish
 *
 * @param format - Dayjs format string (default: 'LLL')
 * @returns Current datetime string in Spanish
 */
export function nowFormatted(format = 'LLL'): string {
  return dayjs().format(format);
}

/**
 * Calendar time (contextual relative time) in Spanish
 *
 * @param date - Date to format
 * @returns Calendar string (e.g., "hoy a las 12:56", "ayer a las 15:30", "23/01/2026")
 */
export function calendarEs(date: Date | string | number): string {
  return dayjs(date).calendar(null, {
    sameDay: '[hoy a las] LT',
    nextDay: '[mañana a las] LT',
    nextWeek: 'dddd [a las] LT',
    lastDay: '[ayer a las] LT',
    lastWeek: '[el] dddd [pasado a las] LT',
    sameElse: 'L',
  });
}
